// NTA size chips: one-click add to cart from product cards (Horizon theme).
//
// Uses the same flow as Horizon's own product form, so the cart drawer opens
// (if "auto open" is on in theme settings), the drawer contents refresh, and the
// header cart count updates without any extra wiring.

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_DELAY = 1400;
// Errors stay up longer so there's time to read them
const ERROR_DELAY = 3500;

/** Section IDs of every cart-items-component on the page (drawer, cart page). */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

/** Fetch the full cart after an add, the same way Horizon's product form does. */
async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');

  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }

  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * The product image flies from the chip to the cart icon, using Horizon's
 * fly-to-cart element (the same one its quick add uses). Follows the theme's
 * "Add to cart animation" setting and skips it for reduced motion.
 */
function flyToCart(button) {
  const enabled = button.closest('[data-nta-chips]')?.dataset.flyToCart === 'true';
  const image = button.dataset.image;
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  if (!enabled || !image || !cartIcon || reducedMotion.matches) return;

  // Setting source and destination before the element is defined would be
  // wiped when it upgrades, so skip if Horizon's script hasn't loaded yet.
  if (!customElements.get('fly-to-cart')) return;

  const flyer = document.createElement('fly-to-cart');
  flyer.classList.add('fly-to-cart--quick');
  flyer.style.setProperty('background-image', `url(${image})`);
  flyer.style.setProperty('--start-opacity', '0');
  flyer.source = button;
  flyer.destination = cartIcon;
  document.body.appendChild(flyer);
}

const resetTimers = new WeakMap();

/**
 * Brief confirmation: the chip changes color and the message bar above the
 * chips shows the text (the chips are too small for words). Then both reset.
 */
function flash(button, state, message) {
  const status = button.closest('[data-nta-chips]')?.querySelector('[data-nta-status]');
  button.dataset.state = state;
  if (status) {
    status.textContent = message;
    status.dataset.state = state;
  }

  for (const el of [button, status]) {
    if (!el) continue;
    clearTimeout(resetTimers.get(el));
    resetTimers.set(
      el,
      setTimeout(() => {
        delete el.dataset.state;
        if (el === status) el.textContent = '';
      }, state === 'error' ? ERROR_DELAY : ADDED_DELAY)
    );
  }
}

async function addToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;

  const productId = button.closest('[data-nta-chips]')?.dataset.productId;
  button.setAttribute('aria-busy', 'true');

  // Tell the theme an add is starting. The drawer and cart icon listen for this
  // event and wait on its promise for the final cart.
  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');

  const sectionIds = getCartSectionIds();
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin',
    });
    const data = await response.json();

    // Shopify returns { status, message, description } when an add fails.
    if (data.status) {
      button.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'nta-size-chips', itemCount: 1, productId },
      });

      flash(button, 'error', 'Size unavailable');
      return;
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'nta-size-chips',
        itemCount: 1,
        productId,
        sections: data.sections,
      },
    });

    flyToCart(button);
    flash(button, 'added', 'Added to cart');
  } catch (error) {
    console.error('[nta-size-chips]', error);
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    flash(button, 'error', "Couldn't add. Try again");
  } finally {
    button.removeAttribute('aria-busy');
  }
}

// One listener for every card on the page, including cards loaded later
// (infinite scroll, filtering). Guarded so it only registers once.
if (!window.__ntaSizeChips) {
  window.__ntaSizeChips = true;

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('.nta-chip') : null;
    if (!button || button.disabled) return;

    // Keep the click from opening the product page.
    event.preventDefault();
    event.stopPropagation();
    addToCart(button);
  });

  // Keyboard: each card's chips are one Tab stop (role="toolbar", a "roving
  // tabindex"). Left/Right move between available chips and wrap around,
  // Home/End jump to the first/last, Enter or Space adds. Whichever chip was
  // last focused or clicked keeps the Tab stop, so Tab returns to it.
  const enabledChips = (toolbar) => [...toolbar.querySelectorAll('.nta-chip:not(:disabled)')];

  const moveTabStop = (toolbar, chip) => {
    for (const other of toolbar.querySelectorAll('.nta-chip')) other.tabIndex = other === chip ? 0 : -1;
  };

  document.addEventListener('focusin', (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.nta-chip') : null;
    const toolbar = chip?.closest('[data-nta-chips]');
    if (chip && toolbar) moveTabStop(toolbar, chip);
  });

  document.addEventListener('keydown', (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.nta-chip') : null;
    const toolbar = chip?.closest('[data-nta-chips]');
    if (!chip || !toolbar) return;

    const chips = enabledChips(toolbar);
    const index = chips.indexOf(chip);
    if (index === -1 || chips.length < 2) return;

    let next;
    if (event.key === 'ArrowRight') next = chips[(index + 1) % chips.length];
    else if (event.key === 'ArrowLeft') next = chips[(index - 1 + chips.length) % chips.length];
    else if (event.key === 'Home') next = chips[0];
    else if (event.key === 'End') next = chips[chips.length - 1];
    else return;

    event.preventDefault();
    moveTabStop(toolbar, next);
    next.focus();
  });
}
